export default class APIHandler {
	/**
	 * The constructor.
	 *
	 * Setting some base values for our API.
	 */
	constructor() {
		this.baseURL = QalaDynamicPricing?.rest;
		this.namespace = 'qala-dynamic-pricing/v1';
		this.abortControllers = {};
	}

	/**
	 * Handles requests to our REST API backend and returns the relevant response.
	 *
	 * @param {string} endpoint The endpoint to call.
	 * @param {Object} params   The parameters object that we want to send.
	 * @param {string} method   The method used for the request. Defaults to GET.
	 */
	async fetchFromAPI( endpoint, params = {}, method = 'GET' ) {
		// Initialize the map for this endpoint if it doesn't exist
		if ( ! this.abortControllers[ endpoint ] ) {
			this.abortControllers[ endpoint ] = new Map();
		}

		// Abort all ongoing requests for this endpoint
		for ( const controller of this.abortControllers[ endpoint ].values() ) {
			controller.abort();
		}

		// Generate a unique identifier for the new request
		const requestId = Date.now(); // Using the current timestamp as a simple unique identifier
		const controller = new AbortController();
		this.abortControllers[ endpoint ].set( requestId, controller );

		// Building the URL with query parameters for GET request
		let url = `${ this.baseURL }${ this.namespace }${ endpoint }`;

		// Add the parameters to the request URL if its a GET request and there are params provided.
		if ( method === 'GET' && Object.keys( params ).length > 0 ) {
			const queryParams = new URLSearchParams( params ).toString();
			url += `?${ queryParams }`;
		}

		// Basic options.
		const options = {
			method,
			headers: { 'Content-Type': 'application/json' },
			signal: controller.signal,
		};

		// If it's a POST request, add the body params instead.
		if ( method === 'POST' ) {
			options.body = JSON.stringify( params );
		}

		// Do the fetch!
		try {
			const response = await fetch( url, options );
			if ( ! response.ok ) {
				throw new Error( `HTTP error! status: ${ response.status }` );
			}
			return await response.json();
		} catch ( error ) {
			if ( error.name !== 'AbortError' ) {
				console.error( 'Error fetching data:', error );  // eslint-disable-line
			}
			return null;
		} finally {
			// Clean up: remove the AbortController for this request
			this.abortControllers[ endpoint ].delete( requestId );
		}
	}
}
